import React, { useState } from "react";
import { COUNTRIES } from "@/data/countries";
import { useSeo } from "@/hooks/useSeo";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function RegisterPage() {
  useSeo({ title: "Create your account", description: undefined, path: "/register", noindex: true });
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", country: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await register(form);
      toast.success("Welcome to CSI!");
      const next = new URLSearchParams(window.location.search).get("next"); const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      nav(safeNext || "/dashboard");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4">
      <form onSubmit={submit} className="card-warm p-8 w-full max-w-md">
        <div className="eyebrow mb-2">New account</div>
        <h1 className="font-display text-3xl mb-6">Join Converged Security Institute</h1>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Name</label>
            <input className="field mb-3" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required autoComplete="given-name" data-testid="register-name"/>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Surname</label>
            <input className="field mb-3" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required autoComplete="family-name" data-testid="register-surname"/>
          </div>
        </div>
        <label className="block text-sm font-semibold mb-1">Country</label>
        <select className="field mb-3" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} required data-testid="register-country">
          <option value="">Select country…</option>
          {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <label className="block text-sm font-semibold mb-1">Email</label>
        <input type="email" className="field mb-3" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email"/>
        <label className="block text-sm font-semibold mb-1">Password</label>
        <input type="password" className="field mb-3" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="register-password"/>
        <p className="text-xs text-zinc-500 mb-4" data-testid="register-faculty-note">Faculty members: please <Link to="/join-faculty" className="underline font-semibold" data-testid="register-join-faculty-link">apply to join the faculty</Link> - instructor access is enabled after admin approval.</p>
        {err && <div className="text-sm text-red-600 mb-3" data-testid="register-error">{err}</div>}
        <button disabled={loading} className="btn-terracotta w-full py-3 rounded-full text-sm font-semibold disabled:opacity-70" data-testid="register-submit-btn">
          {loading ? "Creating…" : "Create account"}
        </button>
        <div className="text-sm text-zinc-600 mt-4 text-center">
          Already a member? <Link to="/login" className="font-semibold text-[var(--terracotta)]">Log in</Link>
        </div>
        <style>{`
          .field { width: 100%; background: #fff; border: 1px solid var(--border-warm); border-radius: 12px; padding: 10px 14px; font-size: 14px; outline: none; }
          .field:focus { border-color: var(--ink); }
        `}</style>
      </form>
    </div>
  );
}
